import { EventEmitter, Injectable } from '@angular/core';
import { CollabFileType } from '@common/collab/enums/collab-file-type.enum';
import { CollabService } from '@portal-core/collab/services/collab.service';
import { CurrentService } from '@portal-core/current/services/current.service';
import { groupBy } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

interface CollabPullFetchFile {
  branchName: string;
  filePath: string;
  projectId: number;
}

/**
 * Represents the change in files that are being pulled or fetched from the remote branch.
 * It is emitted by the `CollabPullFetchService` when files are added or removed from the pull/fetch list.
 */
interface CollabPullFetchFilesChange {
  added?: boolean;
  removed?: boolean;
}

/**
 * A service that handles pulling or fetching collab docs from the remote branch.
 */
@Injectable({
  providedIn: 'root'
})
export class CollabPullFetchService {
  public pullFetchFilesChanged: EventEmitter<CollabPullFetchFilesChange> = new EventEmitter<CollabPullFetchFilesChange>();

  private pullFetchFiles: CollabPullFetchFile[] = [];

  constructor(
    private collabService: CollabService,
    private currentService: CurrentService,
  ) {
  }

  /**
   * Adds a file to the list of files to be pulled or fetched from the remote branch.
   * @param projectId The project id the file belongs to.
   * @param branchName The name of the branch to pull or fetch from.
   * @param filePath The path of the file to be pulled or fetched.
   */
  public addPullOrFetchFile(projectId: number, branchName: string, filePath: string) {
    const exists = this.pullFetchFiles.some(file => file.projectId === projectId && file.branchName === branchName && file.filePath === filePath);
    if (!exists) {
      this.pullFetchFiles.push({ projectId, branchName, filePath });
      this.pullFetchFilesChanged.emit({ added: true });
    }
  }

  /**
   * Clears the list of files to be pulled or fetched from the remote branch.
   */
  public clear() {
    const count = this.pullFetchFiles.length;
    this.pullFetchFiles = [];
    if (count > 0)
      this.pullFetchFilesChanged.emit({ removed: true });
  }

  /**
   * Removes a file from the list of files to be pulled or fetched from the remote branch.
   * @param projectId The project id the file belongs to.
   * @param branchName The name of the branch to pull or fetch from.
   * @param filePath The path of the file to no longer be pulled or fetched.
   */
  public removePullOrFetchFile(projectId: number, branchName: string, filePath: string) {
    const count = this.pullFetchFiles.length;
    this.pullFetchFiles = this.pullFetchFiles.filter(file => file.projectId !== projectId || file.branchName !== branchName || file.filePath !== filePath);
    if (this.pullFetchFiles.length !== count) {
      this.pullFetchFilesChanged.emit({ removed: true });
    }
  }

  /**
   * If there is a current doc then it is pulled from the remote branch.
   * @returns A promise that resolves when the pull is completed. Resolves to null if there is no current doc.
   */
  public pullOrFetchCollabDocsFromRemote(): Promise<any> {
    // If there are no files to pull or fetch
    if (this.pullFetchFiles.length > 0) {
      // Group the files by projectId and branchName
      const groupedFiles = groupBy(this.pullFetchFiles, file => `${file.projectId}-${file.branchName}`);

      // Pull or fetch each group of files
      return Promise.allSettled(
        Object.values(groupedFiles).map(files => {
          const { projectId, branchName } = files[0];

          return firstValueFrom(this.collabService.pullOrFetchCollabDocs$({
            branchName,
            clientId: uuidv4(),
            filePaths: files.map(file => file.filePath),
            projectId,
            fileType: CollabFileType.Workspace,
            userId: this.currentService.getUserId()
          }));
        })
      );
    } else {
      return Promise.resolve();
    }
  }

}
